import React, { CSSProperties, MouseEvent as ReactMouseEvent, ReactNode, useState } from 'react';

import { LocalizedStrings } from '@src/utils/localizedStrings';
import { Services } from '@src/services';
import { ServiceName } from '@src/types/Services';
import { classes, style } from 'typestyle';
import { ADALONG_COLORS } from '@src/styles';
import { IMediaService } from '@src/types/Media';
import { CollectionView, CreatorView, MediaView } from 'common';
import { Browsing } from '@src/types/Browsing';
import { goToMedia } from '@src/pages/Media/utils/mediaRoute';
import { MediaType } from '@adalong/schemas';
import { ProductsLinkedExactIcon } from '../SvgIcons/ProductsLinkedExact';
import { ProductsLinkedSimilarIcon } from '../SvgIcons/ProductsLinkedSimilar';
import { Column } from '../Utils';
import { MediaMark } from '../MediaInfo/MediaMark';
import { MediaLittleData } from '../MediaInfo/MediaLittleData';
import { MediaUsername } from '../MediaInfo/MediaUsername';
import { MediaInfo } from '../MediaInfo';
import translation from './translation';
import { ShortcutActionRow } from './ShortcutActionRow';
import { useQueryInfluencersCreators } from '@src/queries/influencers/useQueryCreators';
import { MediaDiffusionIcon } from '../MediaStatus/MediaDiffusionIcon';
import { MediaRightsIcon } from '../MediaStatus/MediaRightsIcon';
import { MediaContainer, mediaContainerClass } from './MediaContainer';
import { MediaVisual, useMediaVisual } from './MediaVisual';

const DEFAULT_NUMBER_PER_ROW = 4;

// TODO: move in the Media folder in pages
interface Props {
  media: MediaView & {
    collection?: CollectionView;
  };
  key: string;
  onImageError?: () => void;
  onSelect?: (media: MediaView) => void;
  blur?: boolean;
  selected?: boolean;
  isViewedByGuest: boolean;
  lightened?: boolean;
  hideOnImageFailed?: boolean;
  columnsPerRow?: number;
  minMediaWidth?: number;
  /**
   * Button to access to media actions
   */
  actionButton: ReactNode;
  /**
   * A callback to render a shortcut button to the main action
   * depending on the context
   * failed indicate if the media failed to load
   */
  renderShortcutActionButton: (media: MediaView, failed: boolean) => ReactNode;
}

export default function MediaPreview({
  media: { ...media },
  hideOnImageFailed,
  actionButton,
  renderShortcutActionButton,
  columnsPerRow = DEFAULT_NUMBER_PER_ROW,
  minMediaWidth,
  ...props
}: Props) {
  const [mouseOver, setMouseOver] = useState<boolean>(false);
  const visual = useMediaVisual(media, props.onImageError);
  const failed = visual.failed ? lang.failed : undefined;

  const [{ mediaService, browsingService }] = useState(() => ({
    mediaService: Services.get<IMediaService>(ServiceName.MediaService),
    browsingService: Services.get<Browsing>(ServiceName.Browsing),
  }));

  // Checking if a creator is saved as an active influencer
  const { data: influencers = [], error: errorInfluencers, isLoading: loadingInfluencers } = useQueryInfluencersCreators();

  function checkActiveInfluencer(username: string | undefined, creators: CreatorView[]): boolean {
    return creators.some((creator) => creator.username === username && creator.isInfluencer);
  }

  const isInfluencerMedia = checkActiveInfluencer(media.source.user_name, influencers);

  return (
    <MediaContainer
      data-id={media._id}
      data-failed={failed}
      data-hideonfailed={hideOnImageFailed}
      data-blur={!!props.blur}
      onMouseEnter={() => setMouseOver(true)}
      onMouseLeave={() => setMouseOver(false)}
      columnsPerRow={columnsPerRow}
      minMediaWidth={minMediaWidth}
    >
      <div
        className={mediaContainerClass}
        style={isInfluencerMedia ? influencerBorderStyle : undefined}
        onClick={handleClick}
        data-selected={props.selected}
      >
        <MediaVisual media={media} visual={visual}>
          {({ failed: visualFailed }) => (
            <div className={overlayClass} data-failed={visualFailed ? lang.failed : undefined} onClick={onOverlayClick}>
              {props.isViewedByGuest ? (
                <div className={headerClass} />
              ) : (
                mouseOver ? (
                  <div className={classes(headerClass)}>
                    <ShortcutActionRow>{renderShortcutActionButton(media, visualFailed)}</ShortcutActionRow>
                  </div>
                ) : null
              )}
            </div>
          )}
        </MediaVisual>
        {!failed && media.type !== MediaType.Untyped ? (
          <div className={footerClass}>
            <MediaInfo
              autoHeight
              titleLine={
                <Column>
                  <MediaUsername username={media.source.user_name} activeInfluencer={isInfluencerMedia} />
                  <span className={mediaStatusClass(mouseOver, props.isViewedByGuest)}>
                    <MediaDiffusionIcon media={media} />
                    <MediaRightsIcon media={media} />
                  </span>
                </Column>
              }
              actionButtons={mouseOver && !props.isViewedByGuest ? actionButton : <></>}
              littleDataLine={
                <MediaLittleData
                  source={media.source.name}
                  location={media.info?.location?.name}
                  likes={media.info?.social?.likes}
                  date={media.published_at}
                />
              }
              marksLine={
                <>
                  {media.hasActiveExactProducts ? <ProductsLinkedExactIcon style={shopIconStyle} /> : null}

                  {media.hasActiveSimilarProducts ? <ProductsLinkedSimilarIcon style={shopIconStyle} /> : null}

                  <MediaMark
                    marks={mediaService.getMarks(
                      {
                        ...media,
                        tags: mediaService.filterTagsByGroup(media.tags),
                      },
                      ['mentions', 'hashtags', 'tagged', 'ownContent']
                    )}
                  />
                </>
              }
            />
          </div>
        ) : null}
      </div>
    </MediaContainer>
  );

  function onOverlayClick(e: ReactMouseEvent<HTMLDivElement>) {
    if (!e.shiftKey) {
      if (!props.isViewedByGuest && !failed && media.type !== MediaType.Untyped) {
        browsingService.goToPage(goToMedia(window.location, media._id));
      } else {
        window.open(media.url.post, '_blank');
      }
    }
  }

  function handleClick(e: ReactMouseEvent<HTMLDivElement>) {
    if (e.shiftKey && props.onSelect) {
      e.stopPropagation();
      props.onSelect(media);
    }
  }

}

const lang = LocalizedStrings(translation);

const footerClass = style({
  backgroundColor: ADALONG_COLORS.SMOOTHER_BLACK,
  padding: '2px 6px',
  overflow: 'hidden',
  borderBottomLeftRadius: 9,
  borderBottomRightRadius: 9,
});

const shopIconStyle: CSSProperties = {
  verticalAlign: 'text-bottom',
  marginRight: 7,
};

const influencerBorderStyle: CSSProperties = {
  border: 'green 1px solid',
};

const mediaStatusClass = (mouseOver: boolean, currentlyViewed: boolean) =>
  style({
    position: 'absolute',
    top: 4,
    right: mouseOver && !currentlyViewed ? 30 : 2,
  });


const overlayClass = style({
  fontSize: 13,
  flexDirection: 'column',
  justifyContent: 'space-between',
  cursor: 'pointer',
  zIndex: 2,
  opacity: 1,
  visibility: 'visible',
  transition: 'opacity 0.3s',
  position: 'absolute',
  display: 'flex',
  top: 0,
  left: 0,
  width: '100%',
  height: '100%',
  color: 'white',
  textAlign: 'center',
});

const headerClass = style({
  display: 'flex',
  justifyContent: 'flex-end',
});
